import React, { useEffect, useState } from "react";
import { auth, firestore, database } from "../firebase";
import { useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { ref, onValue } from "firebase/database";
import { 
  Plus, 
  LogOut, 
  Cpu, 
  Activity, 
  Settings, 
  ChevronRight,
  LayoutDashboard
} from "lucide-react";

const Dashboard = () => {
  const [devices, setDevices] = useState([]);
  const [statuses, setStatuses] = useState({});
  const navigate = useNavigate();


  // ... (imports remain the same)

useEffect(() => {
  const fetchDevices = async () => {
    if (!auth.currentUser) {
      navigate("/login");
      return;
    }
    const profileRef = doc(firestore, "Profiles", auth.currentUser.uid);
    const profileDoc = await getDoc(profileRef);
    
    if (profileDoc.exists()) {
      const deviceList = profileDoc.data().devices || [];
      setDevices(deviceList);
      
      // Setup Realtime listeners for each device status
      deviceList.forEach(device => {
        const statusRef = ref(database, `${device.espId}/deviceStatus`);
        
        onValue(statusRef, (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.val();
            
            /* FIXED: The ESP32 sends: { lastSeen: timestamp }
               We access data.lastSeen directly.
            */
            const lastSeenValue = data.lastSeen;
            
            setStatuses(prev => ({
              ...prev,
              [device.espId]: lastSeenValue
            }));
          } else {
            // Handle case where deviceStatus doesn't exist yet
            setStatuses(prev => ({
              ...prev,
              [device.espId]: null
            }));
          }
        });
      });
    }
  };
  fetchDevices();
}, [navigate]);

// ... (rest of the component remains the same)

  // useEffect(() => {
  //   const fetchDevices = async () => {
  //     if (!auth.currentUser) {
  //       navigate("/login");
  //       return;
  //     }
  //     const profileRef = doc(firestore, "Profiles", auth.currentUser.uid);
  //     const profileDoc = await getDoc(profileRef);
  //     if (profileDoc.exists()) {
  //       const deviceList = profileDoc.data().devices || [];
  //       setDevices(deviceList);
        
  //       // Setup Realtime listeners for each device status
  //       deviceList.forEach(device => {
  //         const statusRef = ref(database, `${device.espId}/deviceStatus`);
  //         onValue(statusRef, (snapshot) => {
  //           if (snapshot.exists()) {
  //             const data = snapshot.val();
  //             const firstKey = Object.keys(data)[0];
  //             const lastSeen = data[firstKey]?.lastSeen;
              
  //             setStatuses(prev => ({
  //               ...prev,
  //               [device.espId]: lastSeen
  //             }));
  //           }
  //         });
  //       });
  //     }
  //   };
  //   fetchDevices();
  // }, [navigate]);

  const isOnline = (lastSeen) => {
    if (!lastSeen) return false;
    const now = Date.now();
    const diff = (now - lastSeen) / 1000 / 60; // Difference in minutes
    return diff < 2;
  };

  const logout = () => auth.signOut().then(() => navigate("/login"));

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans">
      
      {/* --- TOP NAVIGATION BAR --- */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-600 p-2 rounded-lg shadow-md">
              <Activity className="text-white w-5 h-5" />
            </div>
            <div>
              <h1 className="text-slate-900 font-bold tracking-tight leading-none">ATF</h1>
              <p className="text-[10px] uppercase tracking-widest text-emerald-600 font-bold">Terminal Control</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400">
              <Settings className="w-5 h-5" />
            </button>
            <div className="h-6 w-px bg-slate-200" />
            <button 
              onClick={logout}
              className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-rose-600 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        <header className="mb-12 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <h2 className="text-4xl font-extrabold text-slate-900 tracking-tight">Active Nodes</h2>
            <p className="text-slate-500 mt-2 font-medium">Managing {devices.length} IoT units.</p>
          </div>
          
          <button
            onClick={() => navigate("/add-device")}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-6 py-3 rounded-xl font-bold transition-all active:scale-95 shadow-lg shadow-slate-200"
          >
            <Plus className="w-5 h-5" />
            Deploy New Node
          </button>
        </header>

        {devices.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {devices.map((device, index) => {
              const online = isOnline(statuses[device.espId]);
              return (
                <div 
                  key={index} 
                  className="group bg-white border border-slate-200 rounded-3xl p-8 hover:border-emerald-500/50 hover:shadow-xl hover:shadow-emerald-500/5 transition-all duration-300"
                >
                  <div className="flex justify-between items-start mb-6">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-colors ${online ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                      <Cpu className="w-6 h-6" />
                    </div>
                    
                    <div className={`flex items-center gap-2 px-3 py-1 rounded-full border ${online ? 'bg-emerald-50 border-emerald-100 text-emerald-600' : 'bg-slate-50 border-slate-100 text-slate-400'}`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${online ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                      <span className="text-[10px] font-bold uppercase tracking-widest">
                        {online ? 'Live' : 'Offline'}
                      </span>
                    </div>
                  </div>

                  <h3 className="text-2xl font-bold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">
                    {device.name || "Unnamed Node"}
                  </h3>
                  <p className="font-mono text-xs text-slate-400 uppercase tracking-widest mb-8">
                    ID: {device.espId}
                  </p>

                  <div className="flex items-center justify-between pt-6 border-t border-slate-50">
                    <span className="text-xs font-semibold text-slate-400 italic">
                      {online ? 'System Healthy' : 'Check Connection'}
                    </span>
                    <button
                      onClick={() => navigate(`/device-monitor/${device.espId}`, { state: { relayNames: device.relayNames || [] } })}
                      className="p-3 bg-slate-50 text-slate-900 rounded-xl hover:bg-emerald-600 hover:text-white transition-all group/btn shadow-sm"
                    >
                      <ChevronRight className="w-5 h-5 group-hover/btn:translate-x-1 transition-transform" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-32 border-2 border-dashed border-slate-200 rounded-[3rem] bg-white">
            <LayoutDashboard className="w-12 h-12 text-slate-200 mb-4" />
            <h3 className="text-xl font-bold text-slate-900 mb-2">No active infrastructure</h3>
            <p className="text-slate-500 text-center max-w-xs px-4">
              Deploy your first node to begin monitoring.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default Dashboard;